// Where the render graph goes on screen: the ELK layered layout and the SVG path geometry the
// canvas draws with.
// Pure TypeScript with no React and no DOM, so every geometric decision is testable.
import ElkConstructor from "elkjs/lib/elk-api.js";
import type { ELK, ElkNode, LayoutOptions } from "elkjs/lib/elk-api.js";

import { ELK_WORKER_URL } from "../../globals";
import {
  type ProcessEdge,
  type ProcessGraphNode,
  type ProcessRenderGraph,
  edgeKey,
  terminalKind,
} from "./processGraphModel";

export const STEP_W = 220;
export const STEP_H = 52;
export const TERMINAL_SIZE = 18;
const SVG_MARGIN = 20;

type ReachDir = "forward" | "backward";

// Node ids reachable from `sources` following edges in `dir`:
//   "forward"  walks e.from -> e.to   (downstream from start)
//   "backward" walks e.to   -> e.from (upstream from end)
// Iterative BFS so deep/wide graphs never hit a recursion limit. Self-loops add nothing.
export const reachable = (edges: ProcessEdge[], sources: string[], dir: ReachDir): Set<string> => {
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    if (e.from === e.to) continue;
    const [from, to] = dir === "forward" ? [e.from, e.to] : [e.to, e.from];
    const list = adj.get(from);
    if (list) list.push(to);
    else adj.set(from, [to]);
  }
  const seen = new Set<string>(sources);
  const queue = [...sources];
  for (let i = 0; i < queue.length; i += 1) {
    const v = queue[i];
    for (const w of adj.get(v) ?? []) {
      if (!seen.has(w)) {
        seen.add(w);
        queue.push(w);
      }
    }
  }
  return seen;
};

const sizeForNode = (n: ProcessGraphNode, g: ProcessRenderGraph) =>
  terminalKind(n, g) !== null
    ? { width: TERMINAL_SIZE, height: TERMINAL_SIZE }
    : { width: STEP_W, height: STEP_H };

type EdgeDir = { e: ProcessEdge; reversed: boolean };

export type Point = { x: number; y: number };

// Engine-agnostic layout result: node centers plus routed polylines keyed by the real
// edge key, with every polyline running true-from -> true-to (back-edges already
// flipped). The render pass depends only on this shape, not on the layout engine.
export type GraphLayout = {
  nodePos: Map<string, Point>;
  edgePoints: Map<string, Point[]>;
  width: number;
  height: number;
};

// Process graphs are cyclic (steps repeat), so some edges must point against the top-to-bottom
// flow; the panels call those loops. We choose them ourselves, so the engine receives a DAG:
// a greedy ordering (Eades-Lin-Smyth) weighted by traffic, which reverses as few traversals as it
// can. Busy flows keep pointing down, and on process-shaped test maps the layouts came out ~30%
// narrower with ~20% shorter edges than reversing whatever closed a cycle in depth-first order.
// Start is placed first and End last, so nothing points into Start or out of End; layer
// constraints in runLayout then keep them on the very top and bottom rows.
const orientEdges = (
  nodes: ProcessGraphNode[],
  edges: ProcessEdge[],
  start: string,
  end: string,
): EdgeDir[] => {
  const outgoing = new Map<string, ProcessEdge[]>(nodes.map(n => [n.id, []]));
  const incoming = new Map<string, ProcessEdge[]>(nodes.map(n => [n.id, []]));
  // Traffic between a node and the nodes not yet placed.
  const outflow = new Map<string, number>(nodes.map(n => [n.id, 0]));
  const inflow = new Map<string, number>(nodes.map(n => [n.id, 0]));
  for (const e of edges) {
    if (e.from === e.to) continue;
    outgoing.get(e.from)!.push(e);
    incoming.get(e.to)!.push(e);
    outflow.set(e.from, outflow.get(e.from)! + e.transitionsCount);
    inflow.set(e.to, inflow.get(e.to)! + e.transitionsCount);
  }

  const unplaced = new Set(nodes.map(n => n.id));
  const head: string[] = [];
  const tail: string[] = []; // Filled from the end backwards.
  const place = (id: string, list: string[]) => {
    unplaced.delete(id);
    list.push(id);
    for (const e of outgoing.get(id)!) {
      if (unplaced.has(e.to)) inflow.set(e.to, inflow.get(e.to)! - e.transitionsCount);
    }
    for (const e of incoming.get(id)!) {
      if (unplaced.has(e.from)) outflow.set(e.from, outflow.get(e.from)! - e.transitionsCount);
    }
  };

  place(start, head);
  place(end, tail);
  while (unplaced.size > 0) {
    // Sinks and sources need no reversal wherever the rest goes: sinks go last, sources first.
    let peeled = true;
    while (peeled) {
      peeled = false;
      for (const id of unplaced) {
        if (outflow.get(id) === 0) place(id, tail);
        else if (inflow.get(id) === 0) place(id, head);
        else continue;
        peeled = true;
      }
    }
    // Every node left sits on a cycle. Place next the one whose outgoing traffic most exceeds
    // its incoming: its incoming edges from unplaced nodes are the ones reversed.
    let next: string | undefined;
    let nextBalance = Number.NEGATIVE_INFINITY;
    for (const id of unplaced) {
      const balance = outflow.get(id)! - inflow.get(id)!;
      if (balance > nextBalance) {
        next = id;
        nextBalance = balance;
      }
    }
    if (next !== undefined) place(next, head);
  }

  const rank = new Map([...head, ...tail.reverse()].map((id, index) => [id, index]));
  return edges.map(e => ({
    e,
    reversed: e.from !== e.to && rank.get(e.from)! > rank.get(e.to)!,
  }));
};

// Rows the routed edges cross in total when every step sits as low as its longest chain from
// the top allows. Each crossed row adds a node to the graph simplex placement optimizes, so this
// predicts its cost; ELK's own ranking matched it within 2% on maps up to 60 steps.
const rowsCrossed = (nodes: ProcessGraphNode[], edges: EdgeDir[]): number => {
  const links = edges
    .filter(({ e }) => e.from !== e.to)
    .map(({ e, reversed }) => (reversed ? [e.to, e.from] : [e.from, e.to]));
  const below = new Map<string, string[]>(nodes.map(n => [n.id, []]));
  const unranked = new Map<string, number>(nodes.map(n => [n.id, 0]));
  for (const [from, to] of links) {
    below.get(from)!.push(to);
    unranked.set(to, unranked.get(to)! + 1);
  }
  const row = new Map<string, number>();
  const ready = nodes.map(n => n.id).filter(id => unranked.get(id) === 0);
  for (const id of ready) row.set(id, 0);
  for (const id of ready) {
    for (const to of below.get(id)!) {
      row.set(to, Math.max(row.get(to) ?? 0, row.get(id)! + 1));
      unranked.set(to, unranked.get(to)! - 1);
      if (unranked.get(to) === 0) ready.push(to);
    }
  }
  return links.reduce((sum, [from, to]) => sum + row.get(to)! - row.get(from)!, 0);
};

// Simplex placement took up to ~4s within this budget on process-shaped test maps, and up to
// ~9s just above it; it grows roughly with the square of the rows crossed.
const DETAILED_PLACEMENT_MAX_ROWS_CROSSED = 1000;

// Creating an engine spawns its worker immediately, so instances are made lazily. In the
// browser the layout math runs in a dedicated Web Worker and never blocks the main thread;
// where workers don't exist (jsdom tests) we fall back to the synchronous bundled build.
// `onWorkerError` fires when the worker script itself fails to load (a missing
// /static/elk_worker.js): elkjs' PromisedWorker only wires `onmessage`, so without this
// hook every layout promise stays pending forever and callers just hang.
export const createElk = (onWorkerError?: () => void): Promise<ELK> =>
  typeof Worker === "undefined"
    ? import("elkjs/lib/elk.bundled.js").then(m => new m.default())
    : Promise.resolve(
        new ElkConstructor({
          workerFactory: () => {
            const worker = new Worker(ELK_WORKER_URL);
            worker.onerror = () => onWorkerError?.();
            return worker;
          },
        }),
      );

// ELK layered options, tuned for process maps: same top-to-bottom flow as the old
// dagre layout, but with real crossing minimization and orthogonal edge routing so
// parallel flows read as separate lanes instead of overlapping diagonal sweeps.
const ELK_OPTIONS: LayoutOptions = {
  "elk.algorithm": "layered",
  "elk.direction": "DOWN",
  "elk.edgeRouting": "ORTHOGONAL",
  "elk.spacing.nodeNode": "48",
  "elk.spacing.edgeNode": "20",
  "elk.spacing.edgeEdge": "8",
  "elk.layered.spacing.nodeNodeBetweenLayers": "40",
  "elk.layered.spacing.edgeNodeBetweenLayers": "20",
  "elk.layered.spacing.edgeEdgeBetweenLayers": "8",
  "elk.padding": `[top=${SVG_MARGIN},left=${SVG_MARGIN},bottom=${SVG_MARGIN},right=${SVG_MARGIN}]`,
};

// Large maps reserve many parallel routing lanes. Tighten those gaps and shorten the
// routed edges after placement, keeping node dimensions and orthogonal routing intact.
// This compacts the existing layout rather than repeating expensive simplex placement.
const COMPACT_LAYOUT_OPTIONS: LayoutOptions = {
  "elk.spacing.nodeNode": "24",
  "elk.spacing.edgeNode": "12",
  "elk.spacing.edgeEdge": "5",
  "elk.layered.spacing.nodeNodeBetweenLayers": "28",
  "elk.layered.spacing.edgeNodeBetweenLayers": "12",
  "elk.layered.spacing.edgeEdgeBetweenLayers": "5",
  "elk.layered.compaction.postCompaction.strategy": "EDGE_LENGTH",
};

// Give sibling branches a shared layout container. Matching whole neighbourhoods avoids
// transitively clustering almost the entire process just because it shares a busy hub.
// Prefer shared predecessors, then group remaining nodes by shared successors. Terminals
// alone do not define a meaningful cluster. Indexing edges avoids all-pairs node comparisons.
const clusterSiblingNodes = (
  children: ElkNode[],
  edges: EdgeDir[],
  graph: ProcessRenderGraph,
  options: LayoutOptions,
) => {
  const incoming = new Map(children.map(n => [n.id, new Set<string>()]));
  const outgoing = new Map(children.map(n => [n.id, new Set<string>()]));
  for (const { e, reversed } of edges) {
    const from = reversed ? e.to : e.from;
    const to = reversed ? e.from : e.to;
    incoming.get(to)!.add(from);
    outgoing.get(from)!.add(to);
  }

  const assigned = new Set<string>();
  const ids = new Set(children.map(n => n.id));
  const clusters: ElkNode[] = [];
  for (const neighbours of [incoming, outgoing]) {
    const groups = new Map<string, ElkNode[]>();
    for (const child of children) {
      if (assigned.has(child.id) || child.id === graph.start || child.id === graph.end) continue;
      const adjacent = neighbours.get(child.id)!;
      if (adjacent.size === 0 || adjacent.has(graph.start) || adjacent.has(graph.end)) continue;
      const key = JSON.stringify([...adjacent].sort());
      const group = groups.get(key);
      if (group) group.push(child);
      else groups.set(key, [child]);
    }
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      let id = `__process_cluster_${clusters.length}`;
      while (ids.has(id)) id += "_";
      ids.add(id);
      clusters.push({
        id,
        children: group,
        layoutOptions: {
          ...options,
          "elk.padding": "[top=8,left=8,bottom=8,right=8]",
        },
      });
      for (const child of group) assigned.add(child.id);
    }
  }
  return [...children.filter(n => !assigned.has(n.id)), ...clusters];
};

// How parallel edges attach to nodes: "bundled" routes edges sharing an endpoint through
// one channel that splits where flows diverge; "separate" gives every edge its own channel
// and its own unique attachment point on the node border.
export type EdgeRoutingMode = "bundled" | "separate";

// Keeps only the nodes that lie on a directed start->end path (reachable FROM start AND able to
// reach end, over real from->to directions). Regions severed from the terminals - floating islands,
// dead-end branches - are removed entirely, nodes and edges. The two terminals are always kept, so
// the process endpoints never vanish, and they are laid out by ELK along with everything else:
// explicit layer constraints rank start at the very top and end at the very bottom.
export const runLayout = async (
  graph: ProcessRenderGraph,
  engine: ELK,
  edgeRouting: EdgeRoutingMode = "separate",
) => {
  const reachFromStart = reachable(graph.edges, [graph.start], "forward");
  const reachToEnd = reachable(graph.edges, [graph.end], "backward");
  const keep = new Set<string>();
  for (const n of graph.nodes) {
    if (reachFromStart.has(n.id) && reachToEnd.has(n.id)) keep.add(n.id);
  }
  // Terminals always render, even when pruning severs every incident edge. Added after `keep`
  // is computed so a start->(pruned node) edge can't resurrect the pruned node below.
  keep.add(graph.start);
  keep.add(graph.end);

  const filteredNodes = graph.nodes.filter(n => keep.has(n.id));
  // Both endpoints must survive, so edges into/out of pruned regions (including start/end stubs to a
  // pruned node) disappear too.
  const visibleEdges = graph.edges.filter(e => keep.has(e.from) && keep.has(e.to));

  const oriented = orientEdges(filteredNodes, visibleEdges, graph.start, graph.end);
  const routedEdges = oriented.filter(({ e }) => e.from !== e.to);
  const detailedPlacement =
    rowsCrossed(filteredNodes, routedEdges) <= DETAILED_PLACEMENT_MAX_ROWS_CROSSED;
  const elkGraph: ElkNode = {
    id: "root",
    layoutOptions: {
      ...ELK_OPTIONS,
      ...(!detailedPlacement ? COMPACT_LAYOUT_OPTIONS : {}),
      // Larger maps fall back to linear segments: ~0.5s at 100 steps, but about twice as wide.
      "elk.layered.nodePlacement.strategy": detailedPlacement
        ? "NETWORK_SIMPLEX"
        : "LINEAR_SEGMENTS",
      "elk.layered.thoroughness": detailedPlacement ? "15" : "5",
      "elk.layered.mergeEdges": edgeRouting === "bundled" ? "true" : "false",
    },
    children: filteredNodes.map(n => ({
      id: n.id,
      ...sizeForNode(n, graph),
      // Reversing a cycle can leave other sources/sinks. Give the terminals their own
      // layers so faster placement cannot put a step above Start or below End.
      ...(n.id === graph.start || n.id === graph.end
        ? {
            layoutOptions: {
              "elk.layered.layering.layerConstraint":
                n.id === graph.start ? "FIRST_SEPARATE" : "LAST_SEPARATE",
            },
          }
        : {}),
    })),
    // Parallel a<->b pairs (common around loops) stay separate edges with their own
    // routing channels; the real edge key is kept as the id so the render pass can
    // find each one back.
    edges: routedEdges.map(({ e, reversed }) => ({
      id: edgeKey(e.from, e.to),
      sources: [reversed ? e.to : e.from],
      targets: [reversed ? e.from : e.to],
      // On large maps, raw traffic weights let a few busy edges stretch entire sibling
      // groups apart. Give each connection equal influence there; retain small-map tuning.
      layoutOptions: {
        "elk.layered.priority.straightness": detailedPlacement ? String(e.transitionsCount) : "1",
      },
    })),
  };
  if (!detailedPlacement) {
    elkGraph.children = clusterSiblingNodes(
      elkGraph.children!,
      routedEdges,
      graph,
      elkGraph.layoutOptions!,
    );
    elkGraph.layoutOptions!["elk.hierarchyHandling"] = "INCLUDE_CHILDREN";
  }
  const laidOut = await engine.layout(elkGraph);

  const reversedMap = new Map(oriented.map(o => [edgeKey(o.e.from, o.e.to), o.reversed]));

  // Flatten ELK's result into plain positions/polylines. ELK reports node x/y as the
  // top-left corner while the render pass works in centers. Reversed back-edges were
  // handed to ELK as (to, from); flip their routed points so the stored polyline runs
  // true-from -> true-to and the arrowhead lands on the real destination.
  const nodePos = new Map<string, Point>();
  const edgePoints = new Map<string, Point[]>();
  // Cluster boxes are invisible. Child positions and edge sections are relative to their
  // containing graph, so translate them once into the canvas's root coordinate system.
  const flatten = (parent: ElkNode, offsetX: number, offsetY: number) => {
    for (const child of parent.children ?? []) {
      const x = offsetX + (child.x ?? 0);
      const y = offsetY + (child.y ?? 0);
      if (child.children) flatten(child, x, y);
      else {
        nodePos.set(child.id, {
          x: x + (child.width ?? 0) / 2,
          y: y + (child.height ?? 0) / 2,
        });
      }
    }
    for (const edge of parent.edges ?? []) {
      const points = (edge.sections ?? [])
        .flatMap(s => [s.startPoint, ...(s.bendPoints ?? []), s.endPoint])
        .map(p => ({ x: p.x + offsetX, y: p.y + offsetY }));
      if (points.length === 0) continue;
      if (reversedMap.get(edge.id)) points.reverse();
      edgePoints.set(edge.id, points);
    }
  };
  flatten(laidOut, 0, 0);

  const layout: GraphLayout = {
    nodePos,
    edgePoints,
    width: laidOut.width ?? 0,
    height: laidOut.height ?? 0,
  };
  return { layout, nodes: filteredNodes, edges: visibleEdges, reversedMap };
};

export type ProcessGraphLayoutResult = Awaited<ReturnType<typeof runLayout>>;

const CORNER_R = 7;

// The layout engine already returns routed polylines. We render them as straight segments with small
// rounded corners (a quadratic bend at each interior vertex), so edges read as direct flow
// instead of the large decorative curves the previous Catmull-Rom smoothing produced. The
// corner radius is clamped to half the shorter adjacent segment so short hops don't overshoot.
export const roundedPath = (points: Point[]): string => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M${points[0].x},${points[0].y}`;
  if (points.length === 2) {
    return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;
  }
  const len = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
  const lerp = (a: Point, b: Point, t: number): Point => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  });
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const inLen = len(prev, curr);
    const outLen = len(curr, next);
    const r = Math.min(CORNER_R, inLen / 2, outLen / 2);
    const a = inLen === 0 ? curr : lerp(prev, curr, 1 - r / inLen);
    const b = outLen === 0 ? curr : lerp(curr, next, r / outLen);
    d += ` L${a.x},${a.y} Q${curr.x},${curr.y} ${b.x},${b.y}`;
  }
  const last = points[points.length - 1];
  d += ` L${last.x},${last.y}`;
  return d;
};

/** Base length and half-width of the solid arrowhead a routed edge ends in, in graph units. */
export const ARROWHEAD_LENGTH = 8;
export const ARROWHEAD_HALF_WIDTH = 4.5;
const ARROWHEAD_MIN_LENGTH = 4;

/**
 * How long the head may be, given the straight run available at the end of `points`. A head that
 * eats the whole run begins inside the route's last turn, so the turn reads as a kink under the
 * arrow instead of as an approach: cap it at 60% of the run and leave the rest as landing strip.
 * Runs too short for even that still get a visible — if stubby — head.
 */
export const fitArrowheadLength = (points: Point[], max = ARROWHEAD_LENGTH): number => {
  const last = points[points.length - 1];
  const before = points[points.length - 2];
  if (!last || !before) return 0;
  const run = Math.hypot(last.x - before.x, last.y - before.y);
  return Math.max(Math.min(run * 0.6, max), Math.min(ARROWHEAD_MIN_LENGTH, run * 0.85));
};

/**
 * `points` with the last vertex pulled back along the final segment. The line then stops where the
 * arrowhead starts rather than running underneath it, and — because the corner radius is clamped
 * to half the shortened segment — the last turn is pulled back with it and finishes before the head.
 */
export const trimRouteEnd = (points: Point[], distance: number): Point[] => {
  if (points.length < 2 || distance <= 0) return points;
  const last = points[points.length - 1];
  const before = points[points.length - 2];
  const run = Math.hypot(last.x - before.x, last.y - before.y);
  if (run === 0) return points;
  const t = Math.min(distance, run) / run;
  return [
    ...points.slice(0, -1),
    { x: last.x - (last.x - before.x) * t, y: last.y - (last.y - before.y) * t },
  ];
};

// ELK deliberately receives no self-edges, because they can enlarge or destabilise the layered
// layout. Render a compact orthogonal loop around the step's top-right corner instead. Like the
// routed graph edges, it has three rounded 90-degree turns: up -> right -> down -> left into the
// box. This leaves the node label unobstructed while matching the rest of the map.
export const selfLoopGeometry = (
  center: Point,
): { path: string; points: Point[]; label: Point; focus: Point } => {
  const right = center.x + STEP_W / 2;
  const top = center.y - STEP_H / 2;
  const startX = right - 18;
  const points = [
    { x: startX, y: top },
    { x: startX, y: top - 14 },
    { x: right + 14, y: top - 14 },
    { x: right + 14, y: top + 14 },
    { x: right, y: top + 14 },
  ];
  return {
    path: roundedPath(points),
    points,
    // ProcessGraphCanvas applies its usual +6/-4 text offset to edge-label anchors.
    label: { x: right - 58, y: top - 3 },
    focus: { x: right + 7, y: top + 1 },
  };
};

/**
 * A routed polyline shifted sideways by `distance`, so a route both runs took can be drawn as two
 * adjacent lines rather than one line the two colours take turns on. The routing is axis-aligned,
 * so each corner's offset is just the sum of its two segment normals — no general miter needed.
 */
export const offsetPolyline = (points: Point[], distance: number): Point[] => {
  const normal = (from: Point, to: Point): Point => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    return length === 0 ? { x: 0, y: 0 } : { x: dy / length, y: -dx / length };
  };
  return points.map((point, index) => {
    const before = index > 0 ? normal(points[index - 1], point) : null;
    const after = index < points.length - 1 ? normal(point, points[index + 1]) : null;
    const shift =
      before && after
        ? { x: before.x + after.x, y: before.y + after.y }
        : (before ?? after ?? { x: 0, y: 0 });
    return { x: point.x + shift.x * distance, y: point.y + shift.y * distance };
  });
};

// Solid triangle sitting outside the destination border, apex touching it. Sized in graph units
// so it stays proportional to the cards it points at, and kept proportional when a short final
// run forces the length down.
export const arrowheadPath = (
  tip: { x: number; y: number },
  from: { x: number; y: number },
  length: number,
): string => {
  const halfWidth = (length * ARROWHEAD_HALF_WIDTH) / ARROWHEAD_LENGTH;
  const dx = tip.x - from.x;
  const dy = tip.y - from.y;
  const norm = Math.hypot(dx, dy);
  // A zero-length final segment leaves no direction to point in; fall back to pointing down.
  const ux = norm === 0 ? 0 : dx / norm;
  const uy = norm === 0 ? 1 : dy / norm;
  const baseX = tip.x - ux * length;
  const baseY = tip.y - uy * length;
  const px = -uy * halfWidth;
  const py = ux * halfWidth;
  return `M ${tip.x} ${tip.y} L ${baseX + px} ${baseY + py} L ${baseX - px} ${baseY - py} Z`;
};

/** A stable point a graph-space distance from the source along an orthogonal edge route. */
export const pointAlongPolyline = (points: Point[], distance: number): Point | null => {
  if (points.length === 0) return null;
  let remaining = Math.max(0, distance);
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (length === 0) continue;
    if (remaining <= length) {
      const ratio = remaining / length;
      return { x: from.x + (to.x - from.x) * ratio, y: from.y + (to.y - from.y) * ratio };
    }
    remaining -= length;
  }
  return points[points.length - 1];
};

export const polylineLength = (points: Point[]): number =>
  points.slice(1).reduce((total, point, index) => {
    const previous = points[index];
    return total + Math.hypot(point.x - previous.x, point.y - previous.y);
  }, 0);

/**
 * A transition's route in graph space, self-loop aware: routed polylines come from the layout,
 * while self-loops (which ELK never sees) fall back to the canned loop around their node. Every
 * edge-reading feature goes through this so none of them can forget the self-loop case.
 */
export const edgeRoutePoints = (
  layout: GraphLayout | undefined,
  from: string,
  to: string,
): Point[] | null => {
  if (!layout) return null;
  if (from === to) {
    const center = layout.nodePos.get(from);
    return center ? selfLoopGeometry(center).points : null;
  }
  return layout.edgePoints.get(edgeKey(from, to)) ?? null;
};

/** Midpoint used when a sidebar row asks the viewport to bring a transition into view. */
export const transitionFocusPoint = (
  layout: GraphLayout | undefined,
  from: string,
  to: string,
): Point | null => {
  if (!layout) return null;
  if (from === to) {
    const center = layout.nodePos.get(from);
    return center ? selfLoopGeometry(center).focus : null;
  }
  const points = layout.edgePoints.get(edgeKey(from, to));
  return points ? pointAlongPolyline(points, polylineLength(points) / 2) : null;
};

export type ScreenRect = { left: number; top: number; right: number; bottom: number };
export type ScreenSize = { width: number; height: number };

/** The "Step x/y" flash sits just clear of its cell, above or below. */
export const STEP_POSITION_TIP_W = 100;
export const STEP_POSITION_TIP_H = 26;
export const STEP_POSITION_TIP_GAP = 8;

/**
 * How much of a route runs through a rectangle, as a length in graph units.
 *
 * Used to keep the step-position flash off the edge the travelling dots are on: the label is
 * there to be read while the cue is still running, so whichever side of the cell the route
 * crosses least is the side it takes.
 */
const routeLengthInRect = (points: Point[], rect: ScreenRect): number => {
  let covered = 0;
  const inside = (point: Point) =>
    point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1];
    const end = points[index];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    if (length === 0) continue;
    // Sampling (rather than clipping each segment) keeps this cheap and is precise enough for a
    // choice between two sides.
    const samples = Math.max(2, Math.ceil(length / 4));
    let hits = 0;
    for (let step = 0; step <= samples; step += 1) {
      const t = step / samples;
      if (inside({ x: start.x + dx * t, y: start.y + dy * t })) hits += 1;
    }
    covered += (hits / (samples + 1)) * length;
  }
  return covered;
};

/** Which side of a cell the step-position flash should take, given the route being cued. */
export const stepPositionTipSide = (center: Point, route: Point[] | null): "top" | "bottom" => {
  if (!route || route.length < 2) return "top";
  const halfW = STEP_POSITION_TIP_W / 2;
  const top = center.y - STEP_H / 2 - STEP_POSITION_TIP_GAP;
  const bottom = center.y + STEP_H / 2 + STEP_POSITION_TIP_GAP;
  const above = routeLengthInRect(route, {
    left: center.x - halfW,
    right: center.x + halfW,
    top: top - STEP_POSITION_TIP_H,
    bottom: top,
  });
  const below = routeLengthInRect(route, {
    left: center.x - halfW,
    right: center.x + halfW,
    top: bottom,
    bottom: bottom + STEP_POSITION_TIP_H,
  });
  return below < above ? "bottom" : "top";
};

const EDGE_PREVIEW_INSET = 12;
const EDGE_PREVIEW_GAP = 8;

const overlapsRect = (center: Point, size: ScreenSize, rect: ScreenRect, gap = 0): boolean =>
  center.x + size.width / 2 + gap > rect.left &&
  center.x - size.width / 2 - gap < rect.right &&
  center.y + size.height / 2 + gap > rect.top &&
  center.y - size.height / 2 - gap < rect.bottom;

/** Whether any part of the on-map step cell has reached the viewport. */
export const isStepVisible = (center: Point, size: ScreenSize, viewport: ScreenSize): boolean =>
  center.x + size.width / 2 >= 0 &&
  center.x - size.width / 2 <= viewport.width &&
  center.y + size.height / 2 >= 0 &&
  center.y - size.height / 2 <= viewport.height;

/** Match zoomed-out cells, while keeping previews no larger than a cell at 100% zoom. */
export const edgePreviewScale = (zoom: number): number => Math.min(1, zoom);

/** A map never opens below this zoom: smaller, its cells are unreadable. */
const MIN_FIT_ZOOM = 0.2;

/**
 * The view a map opens at: the whole process, centered, so its true shape reads first. A map
 * that only fits below MIN_FIT_ZOOM opens at that zoom instead; an axis that then overflows
 * starts at the top, or around Start across, and the user pans for the rest.
 */
export const fittedView = (
  graph: ScreenSize,
  viewport: ScreenSize,
  startX: number,
): { k: number; tx: number; ty: number } => {
  const whole = Math.min(viewport.width / graph.width, viewport.height / graph.height) * 0.92;
  const k = Math.max(MIN_FIT_ZOOM, Math.min(whole, 1.5));
  const width = graph.width * k;
  const tx =
    width <= viewport.width
      ? (viewport.width - width) / 2
      : Math.min(0, Math.max(viewport.width - width, viewport.width / 2 - startX * k));
  return { k, tx, ty: Math.max(12, (viewport.height - graph.height * k) / 2) };
};

/**
 * Find where a routed edge first enters the viewport when followed inward from one endpoint.
 * This is the same point where the edge visually leaves the screen when read toward that hidden
 * endpoint, and is therefore a more faithful preview anchor than the off-screen node's centre.
 */
export const edgeViewportCrossing = (
  points: Point[],
  endpoint: "from" | "to",
  viewport: ScreenSize,
): Point | null => {
  const route = endpoint === "from" ? points : [...points].reverse();
  if (route.length < 2) return null;
  const inside = (point: Point) =>
    point.x >= 0 && point.x <= viewport.width && point.y >= 0 && point.y <= viewport.height;
  const onBoundary = (point: Point) =>
    inside(point) &&
    (point.x === 0 || point.x === viewport.width || point.y === 0 || point.y === viewport.height);

  // A route endpoint can sit exactly on the viewport boundary while its node is outside it.
  if (onBoundary(route[0])) return route[0];
  if (inside(route[0])) return null;

  for (let index = 1; index < route.length; index += 1) {
    const start = route[index - 1];
    const end = route[index];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const intersections: Array<{ point: Point; t: number }> = [];
    const addVertical = (x: number) => {
      if (dx === 0) return;
      const t = (x - start.x) / dx;
      const y = start.y + dy * t;
      if (t >= 0 && t <= 1 && y >= 0 && y <= viewport.height) {
        intersections.push({ point: { x, y }, t });
      }
    };
    const addHorizontal = (y: number) => {
      if (dy === 0) return;
      const t = (y - start.y) / dy;
      const x = start.x + dx * t;
      if (t >= 0 && t <= 1 && x >= 0 && x <= viewport.width) {
        intersections.push({ point: { x, y }, t });
      }
    };
    addVertical(0);
    addVertical(viewport.width);
    addHorizontal(0);
    addHorizontal(viewport.height);
    const crossing = intersections.sort((a, b) => a.t - b.t)[0];
    if (crossing) return crossing.point;
  }
  return null;
};

/**
 * Pin an off-screen endpoint preview to the nearest viewport boundary. When map chrome occupies
 * that position, move inward from that boundary: below top controls, above bottom controls, or
 * beside side controls. This preserves the coordinate shared with the edge instead of sliding the
 * preview sideways and disconnecting it from the selected route.
 */
export const edgePreviewPosition = (
  projectedCenter: Point,
  previewSize: ScreenSize,
  viewport: ScreenSize,
  obstacles: ScreenRect[] = [],
): Point => {
  const halfW = previewSize.width / 2;
  const halfH = previewSize.height / 2;
  const minX = Math.min(viewport.width / 2, halfW + EDGE_PREVIEW_INSET);
  const maxX = Math.max(viewport.width / 2, viewport.width - halfW - EDGE_PREVIEW_INSET);
  const minY = Math.min(viewport.height / 2, halfH + EDGE_PREVIEW_INSET);
  const maxY = Math.max(viewport.height / 2, viewport.height - halfH - EDGE_PREVIEW_INSET);
  const clampX = (x: number) => Math.max(minX, Math.min(maxX, x));
  const clampY = (y: number) => Math.max(minY, Math.min(maxY, y));
  const pinned = { x: clampX(projectedCenter.x), y: clampY(projectedCenter.y) };
  const collides = (point: Point) =>
    obstacles.some(obstacle => overlapsRect(point, previewSize, obstacle, EDGE_PREVIEW_GAP));
  if (!collides(pinned)) return pinned;

  const onLeft = projectedCenter.x - halfW < 0;
  const onRight = projectedCenter.x + halfW > viewport.width;
  const onTop = projectedCenter.y - halfH < 0;
  const onBottom = projectedCenter.y + halfH > viewport.height;
  const candidates: Point[] = [];
  for (const obstacle of obstacles.filter(rect =>
    overlapsRect(pinned, previewSize, rect, EDGE_PREVIEW_GAP),
  )) {
    if (onTop) {
      candidates.push({ x: pinned.x, y: clampY(obstacle.bottom + EDGE_PREVIEW_GAP + halfH) });
    }
    if (onBottom) {
      candidates.push({ x: pinned.x, y: clampY(obstacle.top - EDGE_PREVIEW_GAP - halfH) });
    }
    if (onLeft) {
      candidates.push({ x: clampX(obstacle.right + EDGE_PREVIEW_GAP + halfW), y: pinned.y });
    }
    if (onRight) {
      candidates.push({ x: clampX(obstacle.left - EDGE_PREVIEW_GAP - halfW), y: pinned.y });
    }
  }
  const available = candidates.filter(candidate => !collides(candidate));
  return (
    available.sort(
      (a, b) =>
        Math.hypot(a.x - pinned.x, a.y - pinned.y) - Math.hypot(b.x - pinned.x, b.y - pinned.y),
    )[0] ?? pinned
  );
};
